import React, { useState, useEffect, useCallback } from "react";
import { Button } from "react-bootstrap";
import getMatchingCandidateV2 from "../../apis/getMatchingCandidateV2";
import getUpdateProposeFor from "../../apis/getUpdateProposeFor";
import ProfileImageMd from "../../components/CommonComponent/ProfileImageMd";
import { Link } from "react-router-dom";
import "./Accordion.css";
import CandidateScoreTabs from "./CandidateScoreTabs";
import FullCircleChart from "./FullCircleChart";

const AIMatchingUI = ({
  jobSlug,
  user_slug,
  triggerUpdateProposeFor,
  jobDetails,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [dataArray, setDataArray] = useState([]);
  const [filterData, setFilterData] = useState(false);
  const [filterArray, setFilterArray] = useState([
    { name: "Education", value: 0, type: "edu", remove: 0 },
    { name: "Job Titles", value: 0, type: "jt", remove: 0 },
    { name: "Skills", value: 0, type: "sk", remove: 0 },
    { name: "Industries", value: 0, type: "in", remove: 0 },
    { name: "Management Level", value: 0, type: "ml", remove: 0 },
    { name: "Certifications", value: 0, type: "crt", remove: 0 },
    { name: "Executive Type", value: 0, type: "et", remove: 0 },
    { name: "Languages", value: 0, type: "lng", remove: 0 },
  ]);
  const [expandedRowKey, setExpandedRowKey] = useState(null);

  const handleRowToggle = (key) => {
    setExpandedRowKey((prev) => (prev === key ? null : key));
  };

  const getScoreByType = (type) => {
    const item = filterArray.find((el) => el.type === type);
    return item ? item.value : 0;
  };

  const fetchMatchingCandidate = () => {
    setIsLoading(true);
    getMatchingCandidateV2(
      jobSlug,
      user_slug,
      getScoreByType("jt"),
      getScoreByType("edu"),
      getScoreByType("sk"),
      getScoreByType("in"),
      getScoreByType("lng"),
      getScoreByType("ml"),
      getScoreByType("crt"),
      getScoreByType("et")
    )
      .then(({ data }) => {
        if (data?.matches) {
          setDataArray(data.matches);
          setFilterData(data.filters_has_data);
          const updatedFilters = filterArray.map((item) => {
            const hasDataKey = `${item.name.replace(/\s/g, "")}HasData`;
            const valueKey = item.name.replace(/\s/g, "");
            const hasData = data.filters_has_data?.[hasDataKey];
            const value = data.filters_has_data?.[valueKey];
            if (hasData || value) {
              return {
                ...item,
                value: parseInt((value || 0) * 100),
                remove: 0,
              };
            }
            return { ...item, remove: 1 };
          });
          setFilterArray(updatedFilters);
        }
        setIsLoading(false);
      })
      .catch(() => setIsLoading(false));
  };

  useEffect(() => {
    if (jobSlug) fetchMatchingCandidate();
  }, [jobSlug]);

  const updateValue = useCallback(
    (e, type) => {
      const newVal = parseInt(e.target.value);
      const index = filterArray.findIndex((item) => item.type === type);
      const updatedFilters = [...filterArray];
      const position = updatedFilters[index].value < newVal;

      updatedFilters[index] = { ...updatedFilters[index], value: newVal };

      const updated = updatedFilters.map((item, idx) => {
        if (idx !== index) {
          let value = item.value;
          if (position && value > 0) value -= 1;
          else if (!position && value < 100) value += 1;
          return { ...item, value };
        }
        return item;
      });

      setFilterArray(updated);
    },
    [filterArray]
  );

  const displayFilters = useCallback(
    () => (
      <div className="d-flex flex-wrap">
        {filterArray.map(
          (item, index) =>
            !item.remove && (
              <div className="col-2 p-3" key={index}>
                <label
                  htmlFor={`filter-${item.type}`}
                  className="form-label"
                  style={{ fontSize: 11 }}
                >
                  {item.name} ({item.value}%)
                </label>
                <input
                  id={`filter-${item.type}`}
                  type="range"
                  className="form-range"
                  min="0"
                  max="100"
                  value={item.value}
                  onChange={(e) => updateValue(e, item.type)}
                />
              </div>
            )
        )}
      </div>
    ),
    [filterArray, updateValue]
  );

  const updateProposeFor = (autoId, candidateId) => {
    getUpdateProposeFor(jobSlug, candidateId, autoId, user_slug)
      .then(({ data }) => {
        if (data?.status === 1) {
          const updatedArray = dataArray.map((item) =>
            item.profile?.user_id === candidateId
              ? {
                  ...item,
                  propose_for: autoId === null ? data.auto_id : null,
                }
              : item
          );
          setDataArray(updatedArray);
          triggerUpdateProposeFor(true);
        }
      })
      .catch(() => {});
  };

  const updatePropose = (autoId, candidateId) => {
    updateProposeFor(autoId, candidateId);
  };

  return (
    <div className="d-block">
      <div className="alert alert-primary">
        <span>
          Adjust the importance of each category here. This will affect how each
          match is scored.
        </span>
      </div>

      <div className="bg-white bg-gradient rounded d-flex justify-content-between mb-2 align-items-center">
        {filterData && (
          <>
            {displayFilters()}
            <div className="p-3">
              <Button
                variant="primary"
                size="sm"
                onClick={fetchMatchingCandidate}
              >
                Update
              </Button>
            </div>
          </>
        )}
      </div>

      <div className="table-wrapper">
        {/* Header Row */}
        <div
          className="header-row bg-light text-muted fw-bold small px-3 py-2 border-bottom"
          style={{
            borderTopLeftRadius: "0.5rem",
            borderTopRightRadius: "0.5rem",
            display: "flex",
            alignItems: "center",
          }}
        >
          <div style={{ width: "4.5rem" }}>Shortlisted</div>
          <div style={{ width: "4.5rem" }}>Score</div>
          <div className="col-custom">Candidate</div>
          <div className="col-custom">Role</div>
          <div className="col-custom">Location</div>
          <div style={{ width: "6.5rem" }}>Availability</div>
          <div className="col-custom d-flex justify-content-end">Action</div>
        </div>

        {/* Data Rows */}
        {dataArray?.map((item, key) => {
          const profile = item?.profile;
          if (!profile) return null;

          return (
            <div
              key={key.toString()}
              className="border-bottom"
              style={{
                borderLeft:
                  expandedRowKey === key ? "4px solid #0d6efd" : "none",
              }}
            >
              <div className="data-row d-flex align-items-center py-3">
                <div style={{ width: "4.5rem" }}>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={item.propose_for !== null}
                    onChange={() =>
                      updatePropose(item.propose_for, profile.user_id)
                    }
                  />
                </div>
                {/* Score */}
                <div style={{ width: "4.5rem" }}>
                  <span className="badge bg-light text-dark fs-6">
                    {item?.score}
                  </span>
                </div>

                {/* Candidate */}
                <div className="col-custom">
                  <div className="d-flex align-items-center">
                    <ProfileImageMd
                      imgSrc={item?.profile?.profile_image_path}
                    />
                    <div className="ms-3 text-wrap">
                      <div>
                        <span className="fw-bold">{item?.profile?.r_name}</span>
                      </div>
                      <div>
                        <Link to="#">
                          <small
                            className="text-primary"
                            title={item?.profile?.user_email}
                          >
                            {item?.profile?.user_email?.length > 30
                              ? item?.profile?.user_email.substr(0, 30) + "..."
                              : item?.profile?.user_email}
                          </small>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Role */}
                <div className="col-custom text-wrap">
                  <small title={item?.profile?.user_profile_role}>
                    {item?.profile?.user_profile_role?.length > 50
                      ? item?.profile?.user_profile_role.substr(0, 50) + "..."
                      : item?.profile?.user_profile_role}
                  </small>
                </div>

                {/* Location */}
                <div className="col-custom text-wrap">
                  <small className="text-dark">
                    {item?.profile?.city_name
                      ? item?.profile?.city_name + ", "
                      : ""}
                    {item?.profile?.state_name
                      ? item?.profile?.state_name + ", "
                      : ""}
                    {item?.profile?.country_name}
                  </small>
                </div>

                {/* Availability */}
                <div style={{ width: "6.5rem" }}>
                  <FullCircleChart
                    value={parseInt(item?.profile?.availability)}
                    size={40}
                  />
                </div>

                {/* Actions */}
                <div className="col-custom d-flex justify-content-end gap-2">
                  <Link
                    to={`/resources/details/${item?.profile?.user_slug}`}
                    target="_blank"
                  >
                    <Button variant="outline-secondary" size="sm">
                      View Profile
                    </Button>
                  </Link>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handleRowToggle(key)}
                  >
                    View Scoring
                  </Button>
                </div>
              </div>

              {expandedRowKey === key && (
                <CandidateScoreTabs item={item} actualProfileName={""} />
              )}
            </div>
          );
        })}
      </div>
      {/* <div className="table-responsive table-wrapper">
        <table className="table table-bordered">
          <thead className="no-border">
            <tr className="border-top-0">
              <th>Shortlisted</th>
              <th className="p-3">Score</th>
              <th>Candidate</th>
              <th>Role</th>
              <th>Location</th>
              <th>Availability</th>
              <th colSpan={2}>Action</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan="7" className="text-center">
                  Loading...
                </td>
              </tr>
            ) : (
              dataArray.map((item, index) => {
                const profile = item?.profile;
                if (!profile) return null;

                const mailTo = `mailto:${profile.user_email}?subject=${
                  jobDetails.job_title
                }&body=Hi ${
                  profile.first_name
                },%0D%0A%0D%0AWe have identified an opportunity we feel could be a great fit for you.%0D%0A%0D%0AJob Title: ${
                  jobDetails.job_title
                }%0D%0A%0D%0AOverview: ${jobDetails.job_description_raw.replace(
                  /<br\s*\/?>/gm,
                  "%0A"
                )}`;

                return (
                  <React.Fragment key={index}>
                    <tr>
                      <td>
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={item.propose_for !== null}
                          onChange={() =>
                            updatePropose(item.propose_for, profile.user_id)
                          }
                        />
                      </td>
                      <td>
                        <span className="lead fw-bold">{item.score}</span>
                      </td>
                      <td>
                        <div className="d-flex">
                          <ProfileImageMd imgSrc={profile.profile_image_path} />
                          <div className="ms-3">
                            {profile.r_name}
                            <div>
                              <Link
                                to="#"
                                onClick={(e) => {
                                  window.location.href = mailTo;
                                  e.preventDefault();
                                }}
                              >
                                <span
                                  className="text-primary"
                                  title={profile.user_email}
                                >
                                  {profile.user_email.length > 30
                                    ? profile.user_email.slice(0, 30) + "..."
                                    : profile.user_email}
                                </span>
                              </Link>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span title={profile.user_profile_role}>
                          {profile.user_profile_role?.length > 50
                            ? profile.user_profile_role.slice(0, 50) + "..."
                            : profile.user_profile_role}
                        </span>
                      </td>
                      <td>
                        <span>
                          {profile.city_name ? `${profile.city_name}, ` : ""}
                          {profile.state_name || profile.country_name}
                        </span>
                      </td>
                      <td>
                        <div className="progress">
                          <div
                            className={`progress-bar ${
                              profile.availability >= 20 &&
                              profile.availability <= 40
                                ? "bg-success"
                                : "bg-danger"
                            }`}
                            role="progressbar"
                            style={{
                              width: `${(profile.availability * 100) / 40}%`,
                            }}
                            aria-valuenow={profile.availability}
                            aria-valuemin="0"
                            aria-valuemax="40"
                          />
                        </div>
                        <div>
                          <span>{profile.availability} hrs/week</span>
                        </div>
                      </td>
                      <td
                        className="text-nowrap px-2"
                        style={{ minWidth: "70px" }}
                      >
                        <Link
                          to={`/resources/details/${profile.user_slug}`}
                          target="_blank"
                        >
                          <Button variant="warning" size="sm">
                            View Profile
                          </Button>
                        </Link>
                      </td>
                      <td
                        className="text-nowrap px-2"
                        style={{ minWidth: "70px" }}
                      >
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleRowToggle(index)}
                        >
                          View Scoring
                        </Button>
                      </td>
                    </tr>
                    {expandedRowKey === index && (
                      <CandidateScoreTabs item={item} actualProfileName={""} />
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div> */}
    </div>
  );
};

export default AIMatchingUI;
